import {CartService} from './CartService';
import {StyleSettingsService} from './StyleSettingsService';
import {PickupMethod, SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {ShippingOptionModel} from '../models/ShippingOption.model';
import {toShippingRuleOptionModel} from '../utils/shipping';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

export type ShippingRuleOptionModel = ShippingOptionModel & {isPickup: boolean};

export class OrderService {
  private readonly cartService: CartService;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  public destinationWasChanged: boolean = false;

  constructor({
    cartService,
    styleSettingsService,
    siteStore,
    viewerContext,
  }: {
    cartService: CartService;
    styleSettingsService: StyleSettingsService;
    siteStore: SiteStore;
    viewerContext: ViewerContext;
  }) {
    this.cartService = cartService;
    this.styleSettingsService = styleSettingsService;
    this.siteStore = siteStore;
    this.viewerContext = viewerContext;
  }

  public get canShipToDestination() {
    if (this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart)) {
      return this.deliverySectionModel?.canShipToDestination;
    }

    return this.cartService.estimatedTotals?.shippingInfo?.canShipToDestination;
  }

  public get hasShippingRules(): boolean {
    const shouldUseDeliveryOptionsLibInCart = this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart);

    return shouldUseDeliveryOptionsLibInCart
      ? !!this.deliverySectionModel?.options?.length
      : !!this.cartService.estimatedTotals?.shippingInfo?.shippingRule?.shippingOptions.length;
  }

  public get canShip() {
    /* istanbul ignore next */
    return (
      this.hasShippingRules ||
      //todo(guy segev): test delivery error for digital (cart.errors.cant_sell_to_country?)
      /* istanbul ignore next */
      this.cartService.isNonShippableCart ||
      /* istanbul ignore next */
      !this.destinationWasChanged
    );
  }

  public isPickup(): boolean {
    const useDeliveryOptionsLibInCart = this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart);

    if (useDeliveryOptionsLibInCart) {
      return this.cartService.estimatedTotals?.shippingInfo?.selectedCarrierServiceOption?.isPickup;
    }

    const selectedShippingRule = this.getSelectedShippingRule();
    return selectedShippingRule?.isPickup;
  }

  public hasTimeSlots(): boolean {
    const useDeliveryOptionsLibInCart = this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart);
    if (useDeliveryOptionsLibInCart) {
      return this.deliverySectionModel?.hasTimerSlots;
    }
    const selectedShippingRule = this.getSelectedShippingRule();

    return Boolean(selectedShippingRule?.logistics.deliveryTimeSlot);
  }

  public get isPickupPoint(): boolean {
    const useDeliveryOptionsLibInCart = this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart);

    if (useDeliveryOptionsLibInCart) {
      return (
        this.cartService.estimatedTotals?.shippingInfo?.selectedCarrierServiceOption?.pickupMethod ===
        PickupMethod.PICKUP_POINT
      );
    }

    const selectedShippingRule = this.getSelectedShippingRule();
    return selectedShippingRule?.logistics.pickupDetails?.pickupMethod === PickupMethod.PICKUP_POINT;
  }

  private getSelectedShippingRule(): ShippingRuleOptionModel {
    const selectedShippingRuleId = this.cartService.estimatedTotals?.shippingInfo?.selectedCarrierServiceOptionCode;

    return (
      selectedShippingRuleId &&
      /* istanbul ignore next - can't ignore the optional chaining :( */
      this.cartService.estimatedTotals?.shippingInfo.shippingRule?.shippingOptions
        .map(toShippingRuleOptionModel)
        .find((option) => option.id === selectedShippingRuleId)
    );
  }

  public get shippingRuleOptions(): ShippingRuleOptionModel[] {
    if (!this.cartService.estimatedTotals?.shippingInfo?.shippingRule?.shippingOptions) {
      return [];
    }

    return this.cartService.estimatedTotals?.shippingInfo?.shippingRule.shippingOptions.map(toShippingRuleOptionModel);
  }

  public get deliverySectionModel(): DeliverySectionModel {
    return this.cartService.deliverySectionModel;
  }

  public get hasPickupOption(): boolean {
    const useDeliveryOptionsLibInCart = this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart);
    if (useDeliveryOptionsLibInCart) {
      return this.deliverySectionModel?.options.some((option) => option.isPickup);
    }
    return this.shippingRuleOptions.some((option) => option.isPickup);
  }

  public get hasEstimatedDestination(): boolean {
    return !!this.cartService.cartModel?.contactInfo?.address.country;
  }

  public get isEstimatedDestinationIsShippable() {
    return (
      this.hasEstimatedDestination &&
      (this.hasShippingRules || this.cartService.isNonShippableCart || this.destinationWasChanged)
    );
  }

  public get taxOnProduct() {
    return this.siteStore.priceSettings?.taxOnProduct ?? /* istanbul ignore next */ false;
  }

  public get shouldDisplayTax() {
    const {shouldShowTax} = this.styleSettingsService;

    return (
      shouldShowTax &&
      !this.taxOnProduct &&
      (!this.cartService.isNonShippableCart || this.isEstimatedDestinationIsShippable)
    );
  }

  public get minimumOrderAmount() {
    return this.cartService.estimatedTotals?.minimumOrderAmount;
  }

  public get hasShippingDestination() {
    return this.hasEstimatedDestination && (this.canShipToDestination || this.destinationWasChanged);
  }
}
